import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import config from 'constants/config'
import {
  isTourCampaignActive,
  TOUR_CAMPAIGN_CONFIG,
} from 'constants/tours'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import BodyText from 'LuxKit/Typography/BodyText'
import { GERMAN_LOCALE } from 'i18n/util/locale'
import { rem } from 'polished'

interface CountdownTimeParts {
  days: number
  hours: number
  minutes: number
  seconds: number
}

interface BannerImages {
  desktop: { id: string }
  tablet: { id: string }
  mobile: { id: string }
}

const DEFAULT_BANNER_IMAGES: BannerImages = {
  desktop: { id: 'desktop-tours-2027-banner' },
  tablet: { id: 'tablet-tours-2027-banner' },
  mobile: { id: 'mobile-tours-2027-banner' },
}

const GERMAN_BANNER_IMAGES: BannerImages = {
  desktop: { id: 'desktop-tours-2027-banner-de-DE' },
  tablet: { id: 'tablet-tours-2027-banner-de-DE' },
  mobile: { id: 'mobile-tours-2027-banner-de-DE' },
}

function getCountdownTimeParts(endDate: string): CountdownTimeParts | null {
  const endTime = new Date(endDate).getTime()

  if (Number.isNaN(endTime)) {
    return null
  }

  const timeDifference = endTime - Date.now()

  if (timeDifference <= 0) {
    return null
  }

  const totalSeconds = Math.floor(timeDifference / 1000)

  return {
    days: Math.floor(totalSeconds / (24 * 60 * 60)),
    hours: Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60)),
    minutes: Math.floor((totalSeconds % (60 * 60)) / 60),
    seconds: totalSeconds % 60,
  }
}

function formatCountdownValue(value: number): string {
  return String(value).padStart(2, '0')
}

function shouldShowCountdownTimer(
  countdownStartDate: string | undefined,
  endDate: string,
): boolean {
  if (!countdownStartDate) {
    return false
  }

  const countdownStartTime = new Date(countdownStartDate).getTime()
  const endTime = new Date(endDate).getTime()
  const currentTime = Date.now()

  return (
    !Number.isNaN(countdownStartTime) &&
    !Number.isNaN(endTime) &&
    currentTime >= countdownStartTime &&
    currentTime <= endTime
  )
}

function getBannerImages(locale: string): BannerImages {
  return locale === GERMAN_LOCALE
    ? GERMAN_BANNER_IMAGES
    : DEFAULT_BANNER_IMAGES
}

const BannerContent = styled.div`
  position: relative;
`

const BannerImage = styled(Image)`
  padding-top: ${rem(5)};
  padding-bottom: ${rem(20)};
  width: 100%;
`

const CountdownCard = styled.div`
  position: absolute;
  top: ${rem(16)};
  right: ${rem(16)};
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  width: fit-content;
  max-width: calc(100% - ${rem(32)});
  padding: ${rem(10)} ${rem(12)};
  border-radius: 0;
  background-color: #d92d20;
  box-shadow: 0 ${rem(8)} ${rem(18)} rgba(18, 49, 46, 0.22);

  @media (min-width: 768px) {
    top: ${rem(20)};
    right: ${rem(20)};
    padding: ${rem(12)} ${rem(14)};
  }
`

const CountdownItems = styled.div`
  display: flex;
  justify-content: center;
  gap: ${rem(4)};
  flex-wrap: wrap;
`

const CountdownItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: ${rem(38)};
  padding: ${rem(4)} ${rem(5)};

  @media (min-width: 768px) {
    min-width: ${rem(44)};
  }
`

const CountdownValue = styled.span`
  color: white;
  font-size: ${rem(18)};
  font-weight: 700;
  line-height: 1;
`

const CountdownLabel = styled.span`
  margin-top: ${rem(3)};
  color: rgba(255, 255, 255, 0.8);
  font-size: ${rem(10)};
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1;
  text-transform: uppercase;
`

const CountdownPrefix = styled(BodyText)`
  color: white;
`

function ToursVerticalCampaignBanner() {
  const intl = useIntl()
  const region = useAppSelector((state) => state.geo.currentRegionCode)
  const bannerImages = getBannerImages(intl.locale)
  const [countdownTimeParts, setCountdownTimeParts] =
    useState<CountdownTimeParts | null>(() =>
      getCountdownTimeParts(TOUR_CAMPAIGN_CONFIG.endDate),
    )
  const [shouldShowCountdown, setShouldShowCountdown] = useState<boolean>(() =>
    shouldShowCountdownTimer(
      TOUR_CAMPAIGN_CONFIG.countdownStartDate,
      TOUR_CAMPAIGN_CONFIG.endDate,
    ),
  )
  const isCampaignActive = isTourCampaignActive(region, config.BRAND)
  const campaignSearchPath = `/search/tours?destinationId=le_d41d8cd98f00b204e9800998ecf8427e&destinationName=Anywhere&campaigns=${encodeURIComponent(TOUR_CAMPAIGN_CONFIG.searchFilterTag ?? '')}`

  const altText = intl.formatMessage({
    id: 'SAejrbVNxbxcsuqnCS-O',
    defaultMessage: 'Tours Campaign',
    description:
      'Alt text for the reusable tours campaign banner image on the tour page',
  })

  const countdownPrefix = intl.formatMessage({
    id: '-JS0EXR6qwlntzrlou84',
    defaultMessage: 'OFFER ENDS IN',
    description:
      'Prefix text displayed above the countdown timer in the reusable tours campaign banner on the tours vertical page',
  })

  const daysLabel = intl.formatMessage({
    id: 'BYH5HVmdVd-e2U9lhW03',
    defaultMessage: 'Days',
    description:
      'Unit label for days shown in the countdown timer on the reusable tours campaign banner on the tours vertical page',
  })

  const hoursLabel = intl.formatMessage({
    id: 'rTRmmMjcNmKuoXAOBrsi',
    defaultMessage: 'Hours',
    description:
      'Unit label for hours shown in the countdown timer on the reusable tours campaign banner on the tours vertical page',
  })

  const minutesLabel = intl.formatMessage({
    id: 'm7zF82fORvpZdnc_UQ4f',
    defaultMessage: 'Minutes',
    description:
      'Unit label for minutes shown in the countdown timer on the reusable tours campaign banner on the tours vertical page',
  })

  const secondsLabel = intl.formatMessage({
    id: 'yw6F4TbkZnlwvNkbjLvY',
    defaultMessage: 'Seconds',
    description:
      'Unit label for seconds shown in the countdown timer on the reusable tours campaign banner on the tours vertical page',
  })

  useEffect(() => {
    if (!isCampaignActive) {
      return undefined
    }

    const countdownInterval = window.setInterval(() => {
      setCountdownTimeParts(
        getCountdownTimeParts(TOUR_CAMPAIGN_CONFIG.endDate),
      )
      setShouldShowCountdown(
        shouldShowCountdownTimer(
          TOUR_CAMPAIGN_CONFIG.countdownStartDate,
          TOUR_CAMPAIGN_CONFIG.endDate,
        ),
      )
    }, 1000)

    return () => {
      window.clearInterval(countdownInterval)
    }
  }, [isCampaignActive])

  if (!isCampaignActive) {
    return null
  }

  return (
    <Clickable to={campaignSearchPath}>
      <CSSBreakpoint>
        <BannerContent>
          <CSSBreakpoint min="desktop">
            <BannerImage
              image={bannerImages.desktop}
              format="gif"
              alt={altText}
            />
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet" max="tablet">
            <BannerImage
              image={bannerImages.tablet}
              format="gif"
              alt={altText}
            />
          </CSSBreakpoint>
          <CSSBreakpoint max="mobile">
            <BannerImage
              image={bannerImages.mobile}
              format="gif"
              alt={altText}
            />
          </CSSBreakpoint>
          {shouldShowCountdown && countdownTimeParts && (
            <CountdownCard aria-label={countdownPrefix}>
              <CountdownPrefix variant="medium" weight="bold">
                {countdownPrefix}
              </CountdownPrefix>
              <CountdownItems>
                <CountdownItem>
                  <CountdownValue>
                    {formatCountdownValue(countdownTimeParts.days)}
                  </CountdownValue>
                  <CountdownLabel>{daysLabel}</CountdownLabel>
                </CountdownItem>
                <CountdownItem>
                  <CountdownValue>
                    {formatCountdownValue(countdownTimeParts.hours)}
                  </CountdownValue>
                  <CountdownLabel>{hoursLabel}</CountdownLabel>
                </CountdownItem>
                <CountdownItem>
                  <CountdownValue>
                    {formatCountdownValue(countdownTimeParts.minutes)}
                  </CountdownValue>
                  <CountdownLabel>{minutesLabel}</CountdownLabel>
                </CountdownItem>
                <CountdownItem>
                  <CountdownValue>
                    {formatCountdownValue(countdownTimeParts.seconds)}
                  </CountdownValue>
                  <CountdownLabel>{secondsLabel}</CountdownLabel>
                </CountdownItem>
              </CountdownItems>
            </CountdownCard>
          )}
        </BannerContent>
      </CSSBreakpoint>
    </Clickable>
  )
}

export default ToursVerticalCampaignBanner
